import { SafeDictionary } from "@insite/client-framework/Common/Types";
/* Custom Code
import {
    HasProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
*/
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { sendAddToCartEvent } from "@insite/client-framework/OdpEvents/UpdateCartEvents";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
// import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors"; // Store Custom Code
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addLinesToCartHandler from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel, ProductModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
// import Button, { ButtonPresentationProps } from "@insite/mobius/Button"; // Custom Code
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch, useStore } from "react-redux";

/* Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
// import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button"; // Store Custom Code
import ga from "@store/Framework/Common/GoogleAnalytics";
import useExpandProps from "@store/Framework/Common/Hooks/useExpandProps";
import {
    itemsInspectionBulkModalHeadline,
    getItemsInspectionCode,
    getItemsInspectionProduct,
    getItemsInspectionProductUnitOfMeasure,
    isAutoManagedItemsInspectionCartLine,
    isItemsInspectionWarehouseExcluded,
    itemsInspectionPropertyName,
    itemsInspectionPickupBlockedMessage,
} from "@store/Framework/Common/Utilities/itemsInspection";
import { useCanAddToCart, useHasEnoughInventory } from "@store/Framework/Store/Data/Products/ProductsHooks";
import PlusCircleFill from "@store/Library/Components/Icons/PlusCircle/PlusCircleFill";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import useOnDemandInventory from "@store/Framework/Common/Hooks/useOnDemandInventory";
import useLiquidationQuantityMessage from "@store/Library/Hooks/useLiquidationQuantityMessage";
import { css } from "styled-components";
/* Custom Code - End */

/* Store Custom Code - Start */
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
/* Store Custom Code - End */

/* Custom Code - Start */
export interface ProductAddToCartButtonProps
    extends Omit<OwnProps, keyof ButtonPresentationProps | keyof LinkPresentationProps> {}

export const useShowProductAddToCartButton = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);
    const canAddToCart = useCanAddToCart(product, productInfo, configurationCompleted, variantSelectionCompleted);

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart);
};
/* Custom Code - End */

/* Store Custom Code - Start */
import Modal from "@insite/mobius/Modal";
import constants from "@store/constants";
import { useCurrentPage } from "@store/Framework/Store/Data/Pages/PageHooks";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
/* Store Custom Code - End */

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToCartButtonStyles; // Custom Code
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // Custom Code
    overridedStyles?: ProductAddToCartButtonStyles;
    isLink?: boolean;
    // Store Custom Code
    isDiscontinuedCustom?: string;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const session = getSession(state); // Store Custom Code
    const productId = props.productContext.product.id;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isUpdatingCart, // Custom Code
        // Store Custom Code
        allUsersCannotBuy: props.productContext.product.properties.allUsersCannotBuy,
        guestCannotBuy: props.productContext.product.properties.guestCannotBuy,
        currentCart: getCurrentCartState(state).value,
        currentFulfillmentMethod: getCurrentCartState(state).value?.fulfillmentMethod || session.fulfillmentMethod,
        productDetailPath: props.productContext.productInfo.productDetailPath,
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        isGuest: session.isGuest,
        skipItemsInspection: isItemsInspectionWarehouseExcluded(state),
    };
};

const mapDispatchToProps = {};

/* Custom Code - Start */
export interface ProductAddToCartButtonStyles {
    button?: ButtonPresentationProps;
    link?: LinkPresentationProps;
}

export const productAddToCartButtonStyles: ProductAddToCartButtonStyles = {
    button: {
        variant: "secondary", // Store Custom Code
        css: css`
            ${x => x.theme.button.primary.css}
        `,
    },
    link: linkGetActionLinkProps(PlusCircleFill),
};

const normalizeKey = (value?: string | null) => value?.trim().toLowerCase() || "";
const addToCartErrorMessage = translate(
    "Unable to add this item to the cart. Please try again or contact support if the problem continues.",
);
const getAddToCartErrorMessage = (error: unknown) => {
    if (typeof error === "string" && error.trim()) {
        return error;
    }

    if (error && typeof error === "object") {
        if ("errorMessage" in error && typeof error.errorMessage === "string" && error.errorMessage.trim()) {
            return error.errorMessage;
        }

        if (
            "message" in error &&
            typeof error.message === "string" &&
            error.message.trim() &&
            !error.message.startsWith("Unable to resolve inspection product for")
        ) {
            return error.message;
        }
    }

    return addToCartErrorMessage;
};

const isFinalSale = (value: unknown) => value === true || (typeof value === "string" && value.trim().toLowerCase() === "true");
/* Custom Code - End */

const ProductAddToCartButton = ({
    productContext: {
        product,
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, inventory }, // Store Custom Code
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    overridedStyles, // Custom Code
    isLink, // Custom Code
    isUpdatingCart, // Custom Code
    productDetailPath, // Store Custom Code
    isDiscontinuedCustom, // Store Custom Code
    // Store Custom Code
    isAuthenticated,
    isGuest,
    allUsersCannotBuy,
    guestCannotBuy,
    currentCart,
    currentFulfillmentMethod,
    skipItemsInspection,
    ...otherProps
}: Props) => {
    const dispatch = useDispatch();
    const store = useStore<ApplicationState>();
    const toasterContext = useContext(ToasterContext);
    const trackAddToCart = ga.use.trackAddToCart(); // Custom Code
    const styles = useExpandProps(productAddToCartButtonStyles, extendedStyles, overridedStyles); // Custom Code
    const [isLoading, setIsLoading] = useState(false); // Custom Code
    const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
    const [itemsInspectionProduct, setItemsInspectionProduct] = useState<ProductModel | undefined>();
    const { id: productId, allowZeroPricing, quoteRequired, detail, isDiscontinued } = product;

    const addCartLines = (cartLines: CartLineModel[]) =>
        // Wrap existing handler to better control loading/disabled states and to ensure the promise only resolves once
        new Promise<CartLineCollectionModel>((resolve, reject) => {
            let settled = false;

            const resolveOnce = (result: CartLineCollectionModel) => {
                if (!settled) {
                    settled = true;
                    resolve(result);
                }
            };

            const rejectOnce = (error: unknown) => {
                if (!settled) {
                    settled = true;
                    reject(error);
                }
            };

            (dispatch as any)(
                addLinesToCartHandler({
                    apiParameter: {
                        cartId: API_URL_CURRENT_FRAGMENT,
                        cartLineCollection: {
                            cartLines,
                        } as CartLineCollectionModel,
                    },
                    onComplete: (resultProps: { apiResult?: CartLineCollectionModel }) => {
                        if (resultProps.apiResult) {
                            resolveOnce(resultProps.apiResult as CartLineCollectionModel);
                        }
                    },
                    onException: rejectOnce,
                } as any),
            );
        });

    /* Store Custom Code - Start */
    const [isModalVariantSelectionHelpOpen, setIsModalVariantSelectionHelpOpen] = React.useState(false);
    const currentPage = useCurrentPage();
    const isProductDetailsPage = currentPage.type === constants.pageType.productDetails;
    const itemsInspectionCode = skipItemsInspection ? "" : getItemsInspectionCode(product);
    const getInspectionPromptDecision = (cartLines?: CartLineModel[] | null) => {
        if (!itemsInspectionCode || !cartLines?.length) {
            return undefined;
        }

        const matchingProductCartLines = cartLines.filter(
            cartLine =>
                !isAutoManagedItemsInspectionCartLine(cartLine, cartLines) &&
                cartLine.productId === productId &&
                (cartLine.qtyOrdered || 0) > 0,
        );

        if (matchingProductCartLines.length === 0) {
            return undefined;
        }

        return matchingProductCartLines.some(
            cartLine => normalizeKey(getItemsInspectionCode(cartLine)) === normalizeKey(itemsInspectionCode),
        )
            ? "include"
            : "exclude";
    };

    const existingInspectionPromptDecision = getInspectionPromptDecision(currentCart?.cartLines);
    const onDemandInventory = useOnDemandInventory(productId);
    const inventoryToUse = onDemandInventory || inventory;
    const { showAvailableQtyMessage } = useLiquidationQuantityMessage({
        liquidationValue: detail?.name,
        requestedQty: qtyOrdered,
        availableQty: inventoryToUse?.qtyOnHand,
    });
    const itemsInspectionPrice =
        typeof itemsInspectionProduct?.unitListPrice === "number" && Number.isFinite(itemsInspectionProduct.unitListPrice)
            ? itemsInspectionProduct.unitListPrice
            : undefined;
    const itemsInspectionQuantity = Number.isFinite(Number(qtyOrdered)) && Number(qtyOrdered) > 0 ? Number(qtyOrdered) : 0;
    const itemsInspectionTotalPrice =
        typeof itemsInspectionPrice === "number" ? itemsInspectionPrice * itemsInspectionQuantity : undefined;
    const itemsInspectionModalMessage = (
        <>
            {translate("This product requires an inspection item for shipped orders. Add it to the cart as well?")}
            {typeof itemsInspectionPrice !== "undefined" && (
                <>
                    <br />
                    <br />
                    {translate("Pre-inspection unit price")}
                    {translate(":")}
                    {" "}
                    <LocalizedCurrency amount={itemsInspectionPrice} />
                </>
            )}
            {typeof itemsInspectionTotalPrice !== "undefined" && itemsInspectionQuantity > 0 && (
                <>
                    <br />
                    {translate("Pre-inspection total")}
                    {` (${itemsInspectionQuantity})`}
                    {translate(":")}
                    {" "}
                    <LocalizedCurrency amount={itemsInspectionTotalPrice} />
                </>
            )}
        </>
    );
    /* Store Custom Code - End */

    useEffect(() => {
        let cancelled = false;

        if (!itemsInspectionCode) {
            setItemsInspectionProduct(undefined);
            return () => {
                cancelled = true;
            };
        }

        getItemsInspectionProduct(itemsInspectionCode)
            .then(itemsInspectionProductResult => {
                if (!cancelled) {
                    setItemsInspectionProduct(itemsInspectionProductResult);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setItemsInspectionProduct(undefined);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [itemsInspectionCode]);

    if (!useShowProductAddToCartButton(configurationCompleted, variantSelectionCompleted) /* Custom Code */) {
        /* Store Custom Code - Start */
        const modalVariantSelectionHelpClickHandler = () => {
            setIsModalVariantSelectionHelpOpen(true);
        };

        const modalVariantSelectionHelpCloseHandler = () => {
            setIsModalVariantSelectionHelpOpen(false);
        };

        if (isProductDetailsPage) {
            return (
                <>
                    <Modal
                        size={500}
                        headline={translate("Can not add to cart")}
                        isOpen={isModalVariantSelectionHelpOpen}
                        handleClose={modalVariantSelectionHelpCloseHandler}
                    >
                        {translate("AddToCartProductSelectionError")}
                    </Modal>
                    <Button onClick={modalVariantSelectionHelpClickHandler} {...styles.button} {...otherProps}>
                        {labelOverride ?? translate("Add to Cart")}
                    </Button>
                </>
            );
        }
        /* Store Custom Code - End */

        return null;
    }

    const addProductToCart = async (includeItemsInspection: boolean) => {
        onAddToCartClick?.();
        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        /* Store Custom Code - Start */
        if (
            guestCannotBuy &&
            guestCannotBuy.toLowerCase() === "true" &&
            (!isAuthenticated || isGuest) &&
            (!allUsersCannotBuy || allUsersCannotBuy.toLowerCase() !== "true")
        ) {
            toasterContext.addToast({
                body: translate("Guest users can not add this product to cart"),
                messageType: "danger",
            });
            return;
        }
        if (isAuthenticated && allUsersCannotBuy && allUsersCannotBuy.toLowerCase() === "true") {
            toasterContext.addToast({
                body: translate("This product can not be added to cart"),
                messageType: "danger",
            });
            return;
        }
        /* Store Custom Code - End */

        setIsLoading(true); // Custom Code
        try {
            const trackingProductInfos: ProductInfo[] = [
                {
                    productId,
                    qtyOrdered,
                    unitOfMeasure,
                    pricing,
                    inventory,
                    failedToLoadInventory: failedToLoadInventory ? true : undefined,
                    productDetailPath,
                },
            ];
            const sectionOptions =
                configurationSelection && Object.values(configurationSelection).length > 0
                    ? Object.values(configurationSelection)
                          .filter(sectionOptionId => !!sectionOptionId)
                          .map(sectionOptionId => ({ sectionOptionId }))
                    : undefined;
            const cartLineProperties = {
                ...(includeItemsInspection && itemsInspectionCode
                    ? { [itemsInspectionPropertyName]: itemsInspectionCode }
                    : {}),
                ...(subscription ? { productSubscription: JSON.stringify(subscription) } : {}),
                ...(isFinalSale(product.properties.itemNCNR) ? { itemNCNR: "true" } : {}),
            };
            const cartLines: CartLineModel[] = [
                {
                    productId,
                    qtyOrdered,
                    unitOfMeasure,
                    notes,
                    sectionOptions,
                    properties: Object.keys(cartLineProperties).length ? cartLineProperties : undefined,
                } as CartLineModel,
            ];

            if (includeItemsInspection && itemsInspectionCode) {
                const inspectionProduct = await getItemsInspectionProduct(itemsInspectionCode);
                if (!inspectionProduct?.id) {
                    throw new Error(`Unable to resolve inspection product for ${itemsInspectionCode}`);
                }

                const inspectionUnitOfMeasure = getItemsInspectionProductUnitOfMeasure(inspectionProduct);

                trackingProductInfos.push({
                    ...createFromProduct(inspectionProduct),
                    qtyOrdered,
                    unitOfMeasure: inspectionUnitOfMeasure,
                });

                cartLines.push({
                    productId: inspectionProduct.id,
                    qtyOrdered,
                    unitOfMeasure: inspectionUnitOfMeasure,
                } as CartLineModel);
            }

            const cartLineCollection = await addCartLines(cartLines);
            const isQtyAdjusted = cartLineCollection.cartLines?.some(cartLine => !!cartLine?.isQtyAdjusted) ?? false;

            if (showAddToCartConfirmationDialog) {
                toasterContext.addToast({
                    body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={includeItemsInspection} />,
                    messageType: "success",
                    timeoutLength: isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                });
            }

            trackingProductInfos.forEach(({ productId, qtyOrdered }) => {
                sendAddToCartEvent({ productId, qtyOrdered }, store.getState());
            });
            trackAddToCart({ items: cartLineCollection.cartLines }, { productInfos: trackingProductInfos }); // Custom Code
        } catch (error) {
            toasterContext.addToast({
                body: getAddToCartErrorMessage(error),
                messageType: "danger",
            });
        } finally {
            setIsLoading(false); // Custom Code
        }
    };

    const addToCartClickHandler = async () => {
        if (itemsInspectionCode && currentFulfillmentMethod === "Ship") {
            let inspectionPromptDecision = existingInspectionPromptDecision;

            if (!inspectionPromptDecision && currentCart?.lineCount && !currentCart.cartLines) {
                setIsLoading(true);
                try {
                    const awaitableLoadCurrentCart = makeHandlerChainAwaitable(loadCurrentCart);
                    await awaitableLoadCurrentCart({
                        shouldLoadFullCart: false,
                        replaceCart: false,
                        getCartLines: true,
                    })(dispatch as any, store.getState);

                    inspectionPromptDecision = getInspectionPromptDecision(
                        getCurrentCartState(store.getState()).value?.cartLines,
                    );
                } finally {
                    setIsLoading(false);
                }
            }

            if (inspectionPromptDecision === "include") {
                addProductToCart(true).catch(() => undefined);
                return;
            }

            if (inspectionPromptDecision === "exclude") {
                addProductToCart(false).catch(() => undefined);
                return;
            }

            setIsInspectionModalOpen(true);
            return;
        }

        if (itemsInspectionCode && currentFulfillmentMethod === "PickUp") {
            toasterContext.addToast({
                body: translate(itemsInspectionPickupBlockedMessage),
                messageType: "danger",
            });
            return;
        }

        addProductToCart(false).catch(() => undefined);
    };

    const submitInspectionModalHandler = () => {
        setIsInspectionModalOpen(false);
        addProductToCart(true).catch(() => undefined);
    };

    const cancelInspectionModalHandler = () => {
        setIsInspectionModalOpen(false);
        addProductToCart(false).catch(() => undefined);
    };

    return (
        <>
            <TwoButtonModal
                headlineText={translate(itemsInspectionBulkModalHeadline)}
                modalIsOpen={isInspectionModalOpen}
                messageText={itemsInspectionModalMessage}
                cancelButtonText={translate("No")}
                submitButtonText={translate("Yes")}
                onCancel={cancelInspectionModalHandler}
                onSubmit={submitInspectionModalHandler}
                dataTestSelector="itemsInspectionModal"
                submitTestSelector="itemsInspectionModal_yes"
                cancelTestSelector="itemsInspectionModal_no"
            />
            {/* Custom Code - Start */}
            {isLink ? (
                <Link
                    {...styles.link}
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isUpdatingCart || isLoading}
                    {...otherProps}
                >
                    {labelOverride ?? translate("Add to Cart")}
                </Link>
            ) : (
                <>
                    {/* Store Custom Code - Start */}
                    {isProductDetailsPage ? (
                        <Button
                            {...styles.button}
                            onClick={addToCartClickHandler}
                            disabled={
                                qtyOrdered <= 0 ||
                                showAvailableQtyMessage ||
                                isAddToCartButtonDisabled ||
                                isLoading
                            }
                            isLoading={isLoading} // Custom Code
                            {...otherProps}
                        >
                            {labelOverride ?? translate("Add to Cart")}
                        </Button>
                    ) : (
                        <Button {...styles.button} href={productDetailPath} {...otherProps}>
                            {labelOverride ?? translate("View More")}
                        </Button>
                    )}
                    {/* Store Custom Code - End */}
                </>
            )}
            {/* Custom Code - End */}
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
